<template>
  <button
    :style="mobileToggleStyle"
    @click="$emit('toggle-nav')"
    v-show="!openNav"
    class="tw:fixed tw:top-4 tw:left-4 tw:inline-flex tw:h-11 tw:w-11 tw:items-center tw:justify-center tw:rounded-xl tw:border tw:border-white/10 tw:bg-sidebar-bg/95 tw:text-white tw:shadow-lg tw:backdrop-blur tw:md:hidden">
    <span class="tw:sr-only">Toggle navigation</span>
    <IconMenu2 :size="20" />
  </button>

  <div
    v-if="openNav"
    class="tw:fixed tw:inset-0 tw:bg-black/50 tw:backdrop-blur-[1px] tw:md:hidden"
    style="z-index: 1031"
    @click="$emit('toggle-nav')" />

  <aside
    class="tw:fixed tw:top-0 tw:left-0 tw:h-dvh tw:flex tw:w-full tw:max-w-80 tw:flex-col tw:overflow-hidden tw:text-white tw:shadow-2xl tw:transition-transform tw:duration-300 tw:md:inset-y-0 tw:md:h-auto tw:md:w-80 tw:md:max-w-80 tw:md:translate-x-0"
    style="z-index: 1032"
    :inert="autopilot.store.isBarOpen"
    :aria-hidden="autopilot.store.isBarOpen"
    :class="['tw:bg-sidebar-bg!', openNav ? 'tw:translate-x-0' : 'tw:-translate-x-full']">
    <div class="tw:flex tw:h-full tw:flex-col tw:px-3 tw:py-3 tw:md:px-5">
      <div class="tw:relative tw:flex tw:items-start tw:justify-between tw:gap-4 tw:md:block">
        <h1 class="tw:flex-1 tw:text-center tw:md:text-left">
          <RouterLink to="/" v-slot="{ navigate }" custom>
            <a href="/" @click.prevent="navigate()" class="tw:text-center tw:md:block tw:no-underline">
              <BrandLogo tone="light" :size="26" class="tw:my-6">
                <img :src="brand.logos.text" class="navbar-brand-image tw:object-contain" />
              </BrandLogo>
            </a>
          </RouterLink>
          <p class="tw:-mt-4! tw:flex tw:justify-center tw:gap-2 tw:text-xs tw:font-semibold tw:text-white/80">
            <span
              class="tw:inline-flex tw:rounded-md tw:bg-sky-600 tw:px-2 tw:py-0.5 tw:text-[0.6rem] tw:font-semibold tw:text-white"
              >v{{ appVersion }}</span
            >
          </p>
        </h1>

        <div class="tw:absolute tw:top-3 tw:right-3">
          <button
            type="button"
            class="tw:inline-flex tw:h-11 tw:w-11 tw:flex-none tw:items-center tw:justify-center tw:rounded-xl tw:border tw:border-white/10 tw:bg-white/5 tw:text-white tw:md:hidden"
            @click="$emit('toggle-nav')">
            <span class="tw:sr-only">Close navigation</span>
            <IconX :size="20" />
          </button>
        </div>
      </div>

      <div class="sidebar-nav-container tw:flex tw:flex-1 tw:min-h-0 tw:flex-col">
        <div class="tw:flex-1 tw:flex tw:flex-col tw:justify-between tw:overflow-y-auto tw:min-h-0">
          <ul class="tw:flex tw:flex-col tw:gap-0.5 tw:p-0 tw:pb-6 tw:pt-2 tw:md:pt-3">
            <template v-for="nav in navigationData" :key="nav.label">
              <RouterLink :to="nav.to" v-slot="{ navigate, isExactActive }" custom>
                <li class="sidebar-nav-item" :class="isExactActive ? 'active' : ''" @click="handleNavigate(navigate)">
                  <span class="tw:mr-3 tw:inline-flex tw:shrink-0">
                    <component :is="nav.icon" :size="20" />
                  </span>
                  <span class="tw:text-base">{{ nav.label }}</span>
                </li>
                <div :id="nav.teleportId" class="tw:hidden" />
              </RouterLink>
            </template>
          </ul>

          <div class="">
            <div class="form-floating tw:my-4">
              <select
                class="form-select tw:bg-black/10! tw:border-white/10! tw:text-white/50!"
                @change="setLanguage(($event.target as HTMLInputElement).value)"
                v-model="currentLanguage"
                id="floatingSelect">
                <option v-for="(language, code) in availableLanguages" :key="code" :value="code">
                  {{ language.label }} - {{ language.country }}
                </option>
              </select>
              <label for="floatingSelect" class="tw:text-white/50!">{{ $t('language') }}</label>
            </div>

            <ul class="tw:flex tw:flex-col tw:gap-2 tw:px-0 tw:pb-2">
              <RouterLink v-if="showBudget" :to="{ name: 'billing.budget' }" v-slot="{ navigate, isActive }" custom>
                <li class="sidebar-nav-item" :class="isActive ? 'active' : ''" @click="handleNavigate(navigate)">
                  <span class="tw:mr-3 tw:inline-flex tw:shrink-0">
                    <component :is="IconPigMoney" />
                  </span>
                  <span class="tw:text-base">{{ $t('nav.billing') }}</span>
                </li>
              </RouterLink>
              <RouterLink :to="{ name: 'settings' }" v-slot="{ navigate, isActive }" custom>
                <li class="sidebar-nav-item" :class="isActive ? 'active' : ''" @click="handleNavigate(navigate)">
                  <span class="tw:mr-3 tw:inline-flex tw:shrink-0">
                    <component :is="IconSettings" />
                  </span>
                  <span class="tw:text-base">{{ $t('nav.settings') }}</span>
                </li>
              </RouterLink>
              <RouterLink
                v-if="userStore.currentUser?.id"
                :to="{ name: 'settings.accounts.detail', params: { id: userStore.currentUser.id } }"
                v-slot="{ navigate, isActive }"
                custom>
                <li class="sidebar-nav-item" :class="isActive ? 'active' : ''" @click="handleNavigate(navigate)">
                  <span class="tw:mr-3 tw:inline-flex tw:shrink-0">
                    <component :is="IconUserCircle" />
                  </span>
                  <span class="tw:text-base">{{ $t('nav.account') }}</span>
                </li>
              </RouterLink>

              <!-- Autopilot Toggle Button -->
              <li
                class="sidebar-nav-item btn-autopilot-toggle"
                @click="autopilot.toggleBar()"
                :class="{ opened: autopilot.store.isBarOpen }">
                <span class="tw:mr-3 tw:inline-flex tw:shrink-0">
                  <component :is="IconSparkles" />
                </span>
                <span class="tw:text-base">Autopilot</span>
                <span
                  class="ms-auto status-dot"
                  :class="{
                    'status-connected': autopilot.store.isConnected,
                    'status-connecting': autopilot.store.isConnecting,
                    'status-disconnected': autopilot.store.isDisconnected,
                  }"></span>
              </li>
            </ul>
          </div>
        </div>
        <div class="tw:rounded-md tw:px-2.5 tw:py-2 tw:md:px-1.25 tw:md:pt-3">
          <div v-if="showProfile && userStore.currentUser" class="tw:flex tw:items-center tw:gap-2">
            <Avatar class="tw:shrink-0" :name="userStore.currentUser.email ?? '?'" :size="20" />
            <div class="tw:min-w-0 tw:flex-1">
              <div class="tw:truncate tw:text-sm tw:font-semibold tw:text-white">
                {{ userStore.currentUser.customer?.data?.name }}
              </div>
              <div class="tw:max-w-45 tw:truncate tw:text-xs tw:text-white/60">
                {{ userStore.currentUser.email }}
              </div>
            </div>
            <VDropdown placement="top-end" class="tw:ml-auto">
              <button
                type="button"
                class="tw:inline-flex tw:items-center tw:justify-center tw:rounded-md tw:bg-transparent tw:p-0 tw:text-white/80 tw:transition tw:hover:text-white">
                <IconDots :size="20" />
              </button>

              <template #popper>
                <ul class="list-group">
                  <button class="list-group-item list-group-item-action" @click.prevent="$emit('logout')">
                    <IconLogout :size="14" />
                    <span>{{ $t('sign_out') }}</span>
                  </button>
                </ul>
              </template>
            </VDropdown>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { Avatar } from '@prospective/lithium';
import {
  IconDots,
  IconLogout,
  IconMenu2,
  IconPigMoney,
  IconSettings,
  IconUserCircle,
  IconX,
  IconSparkles,
} from '@tabler/icons-vue';

import type { MainNavElement } from '@/router/main_nav';
import { availableLanguages, currentLanguage, setLanguage } from '@/locale/translator';
import BrandLogo from '@/components/generic/BrandLogo.vue';
import { brand } from '@/config/brands';
import { useUserStore } from '@/stores/user';
import { useAutopilot } from '@/composables/useAutopilot';
import { version as pkgVersion } from '../../../package.json';

const props = defineProps<{
  openNav: boolean;
  navigationData: MainNavElement[];
  showBudget: boolean;
  showProfile: boolean;
}>();

const emit = defineEmits<{
  (e: 'toggle-nav'): void;
  (e: 'logout'): void;
}>();

// set by build.yml: the tagged release on master (1.19.2), the short sha on stage (1.19.a1b2c3d).
// absent outside the pipeline, where package.json's patch digit means nothing, so mark it -dev.
const appVersion = import.meta.env.VITE_APP_VERSION || `${pkgVersion.split('.').slice(0, 2).join('.')}-dev`;
const userStore = useUserStore();
const autopilot = useAutopilot();
let lockedScrollY = 0;
let isBodyScrollLocked = false;
const mobileToggleLeft = ref<number | null>(null);

/* ------------------------------- LIFECYCLES ------------------------------- */
onMounted(() => {
  window.addEventListener('resize', syncMobileBodyScrollLock);
  window.addEventListener('resize', updateMobileTogglePosition);
  window.addEventListener('scroll', updateMobileTogglePosition, { passive: true });
  window.visualViewport?.addEventListener('resize', updateMobileTogglePosition);
  window.visualViewport?.addEventListener('scroll', updateMobileTogglePosition);
  updateMobileTogglePosition();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncMobileBodyScrollLock);
  window.removeEventListener('resize', updateMobileTogglePosition);
  window.removeEventListener('scroll', updateMobileTogglePosition);
  window.visualViewport?.removeEventListener('resize', updateMobileTogglePosition);
  window.visualViewport?.removeEventListener('scroll', updateMobileTogglePosition);
  unlockBodyScroll();
});

/* -------------------------------- COMPUTED -------------------------------- */

const mobileToggleStyle = computed(() => {
  if (mobileToggleLeft.value === null) {
    return { zIndex: 1032 };
  }

  return {
    zIndex: 1032,
    left: `${mobileToggleLeft.value}px`,
    right: 'auto',
  };
});

/* --------------------------------- METHODS -------------------------------- */
const isMobileViewport = () => window.innerWidth < 768;

const updateMobileTogglePosition = () => {
  if (!isMobileViewport()) {
    mobileToggleLeft.value = null;
    return;
  }

  const viewport = window.visualViewport;
  const visibleOffsetLeft = viewport?.offsetLeft ?? 0;
  const visibleWidth = viewport?.width ?? window.innerWidth;
  const buttonWidth = 44;
  const margin = 16;
  const nextLeft = visibleOffsetLeft + visibleWidth - buttonWidth - margin;

  mobileToggleLeft.value = Math.max(margin, Math.round(nextLeft));
};

// We need to lock the body scroll when the mobile nav is open to prevent the background from scrolling
const unlockBodyScroll = () => {
  if (!isBodyScrollLocked) {
    return;
  }

  const html = document.documentElement;
  const body = document.body;

  html.style.removeProperty('overflow');
  html.style.removeProperty('overscroll-behavior');
  html.style.removeProperty('height');

  body.style.removeProperty('overflow');
  body.style.removeProperty('overscroll-behavior');
  body.style.removeProperty('position');
  body.style.removeProperty('top');
  body.style.removeProperty('width');
  body.style.removeProperty('height');

  isBodyScrollLocked = false;

  window.scrollTo({ top: lockedScrollY });
  lockedScrollY = 0;
};

const lockBodyScroll = () => {
  if (isBodyScrollLocked) {
    return;
  }

  lockedScrollY = window.scrollY;

  const html = document.documentElement;
  const body = document.body;

  html.style.overflow = 'hidden';
  html.style.overscrollBehavior = 'none';
  html.style.height = '100%';

  body.style.overflow = 'hidden';
  body.style.overscrollBehavior = 'none';
  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.width = '100%';
  body.style.height = '100%';

  isBodyScrollLocked = true;
};

const syncMobileBodyScrollLock = () => {
  if (props.openNav && isMobileViewport()) {
    lockBodyScroll();
    return;
  }

  unlockBodyScroll();
};

const handleMobileClose = () => {
  if (window.innerWidth < 768 && props.openNav) {
    emit('toggle-nav');
  }
};

const handleNavigate = (navigate: () => void) => {
  navigate();
  handleMobileClose();
};

/* ---------------------------------- WATCH --------------------------------- */
watch(
  () => props.openNav,
  () => {
    syncMobileBodyScrollLock();
    updateMobileTogglePosition();
  },
  { immediate: true }
);
</script>

<style scoped>
.navbar-brand-image {
  height: 8rem;
  margin: 0 auto;
  transition: transform 0.2s ease-in-out;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-connected {
  background-color: var(--tblr-success);
}

.status-connecting {
  background-color: var(--tblr-warning);
  animation: pulse 1.5s infinite;
}

.status-disconnected {
  background-color: var(--tblr-danger);
}

@keyframes pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

.btn-autopilot-toggle {
  color: rgba(255, 255, 255, 0.8) !important;
  background-color: transparent !important;
  transition: all 0.2s ease-in-out !important;
}

.btn-autopilot-toggle:hover {
  color: rgba(255, 255, 255, 1) !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.btn-autopilot-toggle.opened {
  color: #1a191c !important;
  background-color: white !important;
}

.btn-autopilot-toggle.opened:hover {
  color: #1a191c !important;
  background-color: rgba(255, 255, 255, 0.9) !important;
}
.form-floating > .form-select,
.form-floating > .form-select option {
  cursor: pointer;
}
</style>
